import { Form, Input, Select } from 'antd';
import Button from 'components/Button';
import useAgronomistList from 'hooks/useAgronomistList';
import { useEffect, useState } from 'react';
import useUpdateUser, {
  UpdateUserPayload,
} from 'screens/users/hooks/useUpdateUserMutation';
import { RizeUser } from 'types/common';
import {
  ROLE_GROUPS,
  Roles,
  ROLES,
  SUPPORTED_COUNTRIES,
} from 'utils/constants';
import { getCountryPhoneCode, removePhoneCountryCode } from 'utils/helpers';
import { validatePhoneNumber } from 'utils/validations';

type UserEditContentProps = {
  data?: RizeUser;
  onSuccess: () => void;
  setLoading: (loading: boolean) => void;
};

const UserEditContent = ({
  data,
  onSuccess,
  setLoading: setParentLoading,
}: UserEditContentProps) => {
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [validatingPhone, setValidatingPhone] = useState(false);
  const [form] = Form.useForm();
  const country = Form.useWatch('country', form);
  const role = Form.useWatch('role', form);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const { agronomistFilterOptions } = useAgronomistList(data?.country, {
    role: 'sr',
    labelKey: 'email',
    valueKey: 'email',
  });

  const { mutate, isPending: isLoading } = useUpdateUser({
    onSuccess: onSuccess,
    onError: () => setDisableSubmit(false),
    onFinish: () => {
      setParentLoading(false);
      setDisableSubmit(true);
    },
  });

  const onSubmitForm = async (value: UpdateUserPayload) => {
    if (!data?.id || !data?.country) return;
    setParentLoading(true);
    mutate({
      id: data.id,
      name: value?.name,
      email: value?.email,
      phoneNumber: getCountryPhoneCode(country) + value?.phoneNumber,
      reportingToUserEmailList: value?.reportingToUserEmailList,
    });
  };

  const { phoneNumber, roles, ...restData } = { ...data };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmitForm}
      onFieldsChange={() => setDisableSubmit(false)}
      initialValues={{
        ...restData,
        role: roles?.[0],
        phoneNumber: removePhoneCountryCode(phoneNumber, restData?.country),
      }}
    >
      <Form.Item
        label="Name"
        name="name"
        rules={[
          {
            required: true,
            message: "Please input user's name",
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        validateTrigger="onBlur"
        rules={[
          {
            type: 'email',
            message: 'Please enter a valid email address',
          },
          {
            required: true,
            message: "Please enter user's email address",
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item label="Password" name="password">
        <Input.Password disabled />
      </Form.Item>

      <Form.Item label="Country" name="country">
        <Select
          options={SUPPORTED_COUNTRIES}
          placeholder="Select Country"
          style={{
            maxWidth: 500,
          }}
          disabled
        />
      </Form.Item>

      <Form.Item
        label="Phone Number"
        name="phoneNumber"
        rules={[
          {
            required: true,
            message: 'Please enter a valid phone number.',
          },
          {
            validator: (_, value) => {
              if (form.getFieldValue('country') === 'Test') {
                return Promise.resolve();
              }
              if (
                !value ||
                value.trim() === '' ||
                value === getCountryPhoneCode(country) ||
                value.replace(getCountryPhoneCode(country), '').trim()
                  .length === 0
              ) {
                return Promise.reject('Please enter a valid phone number.');
              }
              return Promise.resolve();
            },
          },
          {
            validator: (_, value) =>
              validatePhoneNumber(
                value,
                getCountryPhoneCode(country),
                setValidatingPhone,
              ),
          },
        ]}
        validateDebounce={300}
        normalize={(value, prevValue) =>
          /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
        }
      >
        <Input
          prefix={`+${getCountryPhoneCode(country)}`}
          type="tel"
          placeholder="Input phone number here"
        />
      </Form.Item>

      <Form.Item
        label="Role"
        name="role"
        rules={[
          {
            required: true,
            message: 'Please select your role!',
          },
        ]}
      >
        <Select
          options={ROLES}
          disabled
          style={{
            maxWidth: 500,
          }}
        />
      </Form.Item>

      {ROLE_GROUPS.JR_AGS.includes(role as Roles) && (
        <Form.Item
          label="Reporting to"
          name={['reportingToUserEmailList', 0]}
          labelCol={{ span: 24 }}
        >
          <Select
            options={agronomistFilterOptions}
            placeholder="Select direct report of Junior Agronomist"
            style={{
              maxWidth: 500,
            }}
          />
        </Form.Item>
      )}

      <Form.Item>
        <Button
          className="w-100"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={isLoading || disableSubmit || validatingPhone}
        >
          Save Changes
        </Button>
      </Form.Item>
    </Form>
  );
};

export default UserEditContent;
